// src/hooks/useStableSocket.js - Un hook personalizado para manejar conexiones estables
import { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';

// Hook para prevenir el ciclo de reconexiones
export function useStableSocket(options) {
    const {
        url = process.env.REACT_APP_SOCKET_URL || '',
        authToken,
        clientId = null,
        isClientPortal = false,
        enabled = true,
        onConnect = () => { },
        onDisconnect = () => { },
        onError = () => { },
        eventHandlers = {}
    } = options;

    const [isConnected, setIsConnected] = useState(false);
    const socketRef = useRef(null);
    const isInitialMount = useRef(true);
    const reconnectTimer = useRef(null);
    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = 10; // Aumentamos el número máximo de intentos

    // Limpiar temporizadores y conexiones
    const cleanup = () => {
        if (reconnectTimer.current) {
            clearTimeout(reconnectTimer.current);
            reconnectTimer.current = null;
        }

        if (socketRef.current) {
            console.log('Cerrando conexión socket existente...');
            // Remover todos los listeners antes de desconectar
            socketRef.current.offAny();
            socketRef.current.disconnect();
            socketRef.current = null;
        }
    };

    // Función para programar reconexión con backoff exponencial
    const scheduleReconnect = () => {
        if (reconnectAttempts.current >= maxReconnectAttempts) {
            console.error(`Máximo de intentos de reconexión alcanzado (${maxReconnectAttempts})`);
            return;
        }

        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        console.log(`Programando reconexión en ${delay / 1000} segundos (intento ${reconnectAttempts.current + 1}/${maxReconnectAttempts})...`);

        reconnectTimer.current = setTimeout(() => {
            reconnectAttempts.current++;
            initSocket();
        }, delay);
    };

    // Inicializar una conexión de socket
    const initSocket = () => {
        // Si ya hay una conexión activa, no hacer nada
        if (socketRef.current?.connected) {
            console.log('Socket ya conectado, omitiendo inicialización');
            return;
        }

        // Limpiar cualquier socket y temporizador previo
        cleanup();

        // Si la conexión no está habilitada, no continuar
        if (!enabled || !authToken) {
            console.log('Conexión de socket no habilitada o falta token');
            return;
        }

        console.log('Iniciando nueva conexión socket...');

        // Preparar datos de autenticación
        const authData = { token: authToken };
        if (isClientPortal && clientId) {
            authData.clientId = clientId;
        }

        // Crear nuevo socket con opciones mejoradas
        socketRef.current = io(url, {
            auth: authData,
            reconnection: true, // Activamos la reconexión automática de Socket.io
            reconnectionAttempts: 3, // Solo intentará 3 veces, luego usaremos nuestra lógica
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            transports: ['websocket', 'polling']
        });

        // Manejar conexión exitosa
        socketRef.current.on('connect', () => {
            console.log('Socket conectado exitosamente', socketRef.current.id);
            setIsConnected(true);
            reconnectAttempts.current = 0; // Resetear contador al conectar con éxito
            onConnect(socketRef.current);
        });

        // Manejar errores de conexión
        socketRef.current.on('connect_error', (error) => {
            console.error('Error de conexión socket:', error.message);
            setIsConnected(false);
            onError(error);

            // Si falla la conexión, programar reconexión
            scheduleReconnect();
        });

        // Manejar desconexión
        socketRef.current.on('disconnect', (reason) => {
            console.log(`Socket desconectado. Razón: ${reason}`);
            setIsConnected(false);
            onDisconnect(reason);

            // Solo reconectar para ciertos tipos de desconexión
            if (reason === 'io server disconnect' || reason === 'transport close' || reason === 'ping timeout') {
                scheduleReconnect();
            }
        });

        // Registrar los manejadores de eventos personalizados
        Object.entries(eventHandlers).forEach(([event, handler]) => {
            socketRef.current.on(event, handler);
        });
    };

    // Efecto para manejar la conexión inicial y limpieza
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (enabled && authToken) {
                initSocket();
            }
        }

        // Limpiar al desmontar
        return () => {
            console.log('Limpiando conexión socket (desmontaje)');
            cleanup();
        };
    }, []); // Este efecto solo se ejecuta una vez al montar

    // Efecto para manejar cambios en las dependencias
    useEffect(() => {
        // No ejecutar en el montaje inicial (ya lo hizo el efecto anterior)
        if (!isInitialMount.current) {
            if (enabled && authToken) {
                // Si cambian las dependencias, iniciar una nueva conexión
                initSocket();
            } else {
                // Si la conexión ya no está habilitada, limpiar
                cleanup();
            }
        }
    }, [enabled, authToken, clientId, isClientPortal, url]); // Dependencias críticas

    // Método para conectar/reconectar manualmente
    const connect = () => {
        if (!enabled || !authToken) return;
        reconnectAttempts.current = 0; // Resetear contador de intentos
        initSocket();
    };

    // Método para desconectar manualmente
    const disconnect = () => {
        cleanup();
    };

    // Método para enviar un evento
    const emit = (event, data) => {
        if (socketRef.current?.connected) {
            socketRef.current.emit(event, data);
            return true;
        } else {
            console.warn('No se puede emitir evento, socket no conectado:', event);
            // Intentar reconectar automáticamente
            if (enabled && authToken) {
                initSocket();
            }
            return false;
        }
    };

    // Verificar conexión periódicamente
    useEffect(() => {
        if (!enabled || !authToken) return;

        const checkInterval = setInterval(() => {
            if (!socketRef.current?.connected && reconnectTimer.current === null) {
                console.log('Conexión de socket perdida, intentando reconectar...');
                connect();
            }
        }, 30000); // Verificar cada 30 segundos

        return () => clearInterval(checkInterval);
    }, [enabled, authToken]);

    return {
        socket: socketRef.current,
        isConnected,
        connect,
        disconnect,
        emit
    };
}
export default useStableSocket;